/**
 * AdminImmeubleExpenseModal — Saisie de dépense (facture rénovation, entretien…).
 *
 * Extrait de AdminImmeubleModals.jsx (Batch #14.221).
 */
import { useState, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, Wallet, Calendar, Receipt, Paperclip, FileCheck2, X } from "lucide-react";
import { useCascadingSelectors, MasterPropertyUnitSelectors, ModalShell, EXPENSE_CATEGORIES, TAX_TYPES } from "./_shared";
import { API, authH } from "@/api";


export function AdminImmeubleExpenseModal({ open, onClose, initialMasterId = "", lockedMaster = false, onCreated }) {
  const ctx = useCascadingSelectors({ initialMasterId });
  const [form, setForm] = useState({
    amount_gross: "",
    tps_amount: "",
    tvq_amount: "",
    tax_type: "tps_tvq",
    category: "entretien_reparation",
    invoice_date: new Date().toISOString().slice(0, 10),
    invoice_number: "",
    vendor: "",
    internal_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  // Batch #14.6 — Pièce justificative (PDF/image)
  const [attachment, setAttachment] = useState(null); // {file, name, size}
  const [uploadedReceipt, setUploadedReceipt] = useState(null); // {drive_file_id, drive_web_view_link}
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setForm({
      amount_gross: "", tps_amount: "", tvq_amount: "",
      tax_type: "tps_tvq", category: "entretien_reparation",
      invoice_date: new Date().toISOString().slice(0, 10),
      invoice_number: "", vendor: "", internal_notes: "",
    });
    setAttachment(null);
    setUploadedReceipt(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowed.includes(f.type)) {
      toast.error("Format non supporté. Accepté : PDF, JPG, PNG, WebP");
      e.target.value = "";
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Fichier > 20 MB");
      e.target.value = "";
      return;
    }
    setAttachment({ file: f, name: f.name, size: f.size });
    setUploadedReceipt(null);
  };

  const removeAttachment = () => {
    setAttachment(null);
    setUploadedReceipt(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const amount_total = useMemo(() => {
    const a = parseFloat(form.amount_gross) || 0;
    const t = parseFloat(form.tps_amount) || 0;
    const q = parseFloat(form.tvq_amount) || 0;
    return (a + t + q).toFixed(2);
  }, [form.amount_gross, form.tps_amount, form.tvq_amount]);

  const submit = async () => {
    if (!ctx.masterId || !ctx.propertyId || !form.amount_gross || !form.invoice_date || !form.category) {
      toast.error("Champs requis : client, immeuble, montant, date, catégorie");
      return;
    }
    setSubmitting(true);
    try {
      // Batch #14.6 — Upload de la pièce justificative d'abord (si présente)
      let receiptInfo = uploadedReceipt;
      if (attachment && !uploadedReceipt) {
        setUploadingReceipt(true);
        const fd = new FormData();
        fd.append("file", attachment.file);
        fd.append("master_id", ctx.masterId);
        const ru = await axios.post(`${API}/admin/pms/expenses/upload-receipt`, fd, {
          headers: { ...authH() },  // PAS de Content-Type manuel → axios met le bon boundary
        });
        receiptInfo = {
          drive_file_id: ru.data?.drive_file_id,
          drive_web_view_link: ru.data?.drive_web_view_link,
          filename: ru.data?.filename,
        };
        setUploadedReceipt(receiptInfo);
        setUploadingReceipt(false);
      }

      const r = await axios.post(`${API}/admin/pms/expenses`, {
        master_id: ctx.masterId,
        building_id: ctx.propertyId,  // backend attend "building_id"
        unit_id: ctx.unitId || null,
        amount_gross: parseFloat(form.amount_gross),
        tps_amount: parseFloat(form.tps_amount) || 0,
        tvq_amount: parseFloat(form.tvq_amount) || 0,
        amount_total: parseFloat(amount_total),
        tax_type: form.tax_type,
        category: form.category,
        invoice_date: form.invoice_date,
        invoice_number: form.invoice_number || null,
        vendor: form.vendor || "",
        internal_notes: form.internal_notes || null,
        drive_receipt_file_id: receiptInfo?.drive_file_id || null,
        drive_receipt_url: receiptInfo?.drive_web_view_link || null,
      }, { headers: authH() });
      toast.success(receiptInfo
        ? `Dépense + facture enregistrées — ${amount_total} $`
        : `Dépense enregistrée — ${amount_total} $`);
      onCreated?.(r.data);
      reset();
      onClose?.();
    } catch (e) {
      setUploadingReceipt(false);
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Saisie d'une dépense"
      subtitle="Facture pour un immeuble client (réparation, entretien, taxes, etc.)"
      icon={Wallet}
      iconBg="bg-amber-500"
      testId="admin-expense-modal"
    >
      <div className="space-y-4">
        <MasterPropertyUnitSelectors ctx={ctx} includeUnitOptional={true} lockedMaster={lockedMaster} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-100">
          <div className="md:col-span-1">
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Montant HT *</label>
            <input
              type="number" step="0.01" min="0"
              value={form.amount_gross}
              onChange={(e) => setForm({ ...form, amount_gross: e.target.value })}
              placeholder="150.00"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-expense-amount-gross"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">TPS</label>
            <input
              type="number" step="0.01" min="0"
              value={form.tps_amount}
              onChange={(e) => setForm({ ...form, tps_amount: e.target.value })}
              placeholder="7.50"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-expense-tps"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">TVQ</label>
            <input
              type="number" step="0.01" min="0"
              value={form.tvq_amount}
              onChange={(e) => setForm({ ...form, tvq_amount: e.target.value })}
              placeholder="14.96"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-expense-tvq"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Total TTC</label>
            <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-700" data-testid="admin-expense-amount-total">
              {amount_total} $
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Type de taxe</label>
            <select
              value={form.tax_type}
              onChange={(e) => setForm({ ...form, tax_type: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
              data-testid="admin-expense-tax-type"
            >
              {TAX_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Catégorie *</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
              data-testid="admin-expense-category"
            >
              {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Date facture *
            </label>
            <input
              type="date"
              value={form.invoice_date}
              onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-expense-date"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Fournisseur</label>
            <input
              type="text"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              placeholder="ex : Plomberie ABC"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-expense-vendor"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">N° facture</label>
            <input
              type="text"
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              placeholder="ex : F-2026-0123"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-expense-invoice-number"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Notes internes</label>
          <textarea
            rows={2}
            value={form.internal_notes}
            onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
            placeholder="Détails / commentaires pour la comptabilité"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="admin-expense-notes"
          />
        </div>

        {/* Batch #14.6 — Pièce justificative obligatoire pour conformité comptable */}
        <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 p-4" data-testid="admin-expense-attachment-zone">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-amber-600" />
              <div>
                <div className="text-xs font-bold text-slate-800">Pièce justificative</div>
                <div className="text-[10px] text-slate-500">PDF, JPG, PNG, WebP — max 20 MB</div>
              </div>
            </div>
            {!attachment ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="admin-expense-attach-pick"
              >
                <Paperclip className="w-3.5 h-3.5" /> Joindre la facture
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <FileCheck2 className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold text-slate-700 max-w-[220px] truncate" title={attachment.name}>{attachment.name}</span>
                <span className="text-slate-500">({(attachment.size / 1024).toFixed(1)} KB)</span>
                <button
                  type="button"
                  onClick={removeAttachment}
                  className="text-rose-600 hover:text-rose-700 ml-1"
                  data-testid="admin-expense-attach-remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/jpg,image/webp"
            onChange={handleFileChange}
            className="hidden"
            data-testid="admin-expense-attach-input"
          />
          {uploadedReceipt && (
            <div className="mt-2 text-[10px] text-emerald-700 inline-flex items-center gap-1">
              <FileCheck2 className="w-3 h-3" /> Uploadé sur Drive : <a href={uploadedReceipt.drive_web_view_link} target="_blank" rel="noopener noreferrer" className="underline">voir le fichier</a>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="admin-expense-cancel">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={submitting || uploadingReceipt}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="admin-expense-submit"
          >
            {(submitting || uploadingReceipt) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            {uploadingReceipt ? "Upload Drive..." : "Enregistrer la dépense"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

